import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { settings as settingsApi } from "../../../../services/admin";
import { ensurePermission } from "../../../../utils/permissions";
import RIcon from "../../../../components/RIcon";

const SMTP_KEYS = [
  "smtp_host",
  "smtp_port",
  "smtp_user",
  "smtp_password",
  "smtp_from_name",
  "smtp_use_ssl",
] as const;

// 订阅抓取国外站点（FDA/EMA 等）经此 HTTP 代理出网；后端 proxy_config.py 每次
// 出网都从 DB 读最新值，所以在这里改完保存即时生效，不用重启 pod。
const PROXY_KEYS = ["proxy_enabled", "http_proxy_url", "proxy_no_proxy"] as const;

export default function SystemSettingsPage() {
  const { user } = useOutletContext<{ user: import("../../../../services/auth").UserInfo | null }>();
  const [toast, setToast] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [smtpValues, setSmtpValues] = useState<Record<string, string>>({});
  const [smtpDirty, setSmtpDirty] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [proxyValues, setProxyValues] = useState<Record<string, string>>({});
  const [proxyDirty, setProxyDirty] = useState(false);
  const [proxySaving, setProxySaving] = useState(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  }, []);

  const loadSettings = useCallback(async () => {
    if (!ensurePermission(user, "settings:read", showToast)) {
      setPageLoading(false);
      return;
    }
    try {
      setPageLoading(true);
      const data = await settingsApi.get();
      const smtp: Record<string, string> = {};
      for (const k of SMTP_KEYS) {
        smtp[k] = data[k] ?? "";
      }
      setSmtpValues(smtp);
      setSmtpDirty(false);
      const proxy: Record<string, string> = {};
      for (const k of PROXY_KEYS) {
        proxy[k] = data[k] ?? "";
      }
      setProxyValues(proxy);
      setProxyDirty(false);
    } catch (err) {
      console.error("Failed to load settings:", err);
      showToast("加载设置失败");
    } finally {
      setPageLoading(false);
    }
  }, [showToast, user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSmtp = (key: string, value: string) => {
    setSmtpValues((prev) => ({ ...prev, [key]: value }));
    setSmtpDirty(true);
  };

  const saveSmtp = async () => {
    if (!ensurePermission(user, "settings:write", showToast)) return;
    setSmtpSaving(true);
    try {
      await settingsApi.update(smtpValues);
      setSmtpDirty(false);
      showToast("SMTP 设置已保存");
    } catch (err) {
      console.error("Failed to save SMTP:", err);
      showToast("保存失败，请检查配置");
    } finally {
      setSmtpSaving(false);
    }
  };

  const updateProxy = (key: string, value: string) => {
    setProxyValues((prev) => ({ ...prev, [key]: value }));
    setProxyDirty(true);
  };

  const saveProxy = async () => {
    if (!ensurePermission(user, "settings:write", showToast)) return;
    setProxySaving(true);
    try {
      await settingsApi.update(proxyValues);
      setProxyDirty(false);
      showToast("网络代理设置已保存");
    } catch (err) {
      console.error("Failed to save proxy:", err);
      showToast("保存失败，请检查配置");
    } finally {
      setProxySaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#718096]">
        <RIcon name="loader-4-line" className="animate-spin text-lg mr-2" />
        加载设置...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#0EA5A0] text-white text-sm px-4 py-2.5 rounded-lg shadow-md">
          <RIcon name="checkbox-circle-line" className="mr-2" />
          {toast}
        </div>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-[#1A2B3C]">系统设置</h1>
          <p className="text-sm text-[#718096] mt-0.5">
            邮箱发送、模型路由等运行配置；管理员可在此编辑。
          </p>
        </div>
        <button
          onClick={() => loadSettings()}
          className="flex items-center gap-2 px-4 h-9 bg-white border border-gray-200 hover:border-[#0EA5A0] text-[#4A5568] hover:text-[#0EA5A0] text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap"
        >
          <RIcon name="refresh-line" />
          刷新
        </button>
      </div>

      {/* SMTP 邮箱配置 */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#1A2B3C]">邮件发送（SMTP）</h3>
            <p className="text-xs text-[#718096] mt-1">
              注册审核通知、订阅完成推送等会用此 SMTP 发件。建议使用 QQ 邮箱授权码。
            </p>
          </div>
          <button
            onClick={saveSmtp}
            disabled={!smtpDirty || smtpSaving}
            className={`flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg whitespace-nowrap ${
              !smtpDirty || smtpSaving
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#0EA5A0] text-white hover:bg-[#0D9590] cursor-pointer"
            }`}
          >
            {smtpSaving ? <RIcon name="loader-4-line" className="animate-spin" /> : <RIcon name="save-line" />}
            保存
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">SMTP 服务器</label>
            <input
              type="text"
              value={smtpValues.smtp_host ?? ""}
              onChange={(e) => updateSmtp("smtp_host", e.target.value)}
              placeholder="smtp.qq.com"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">端口</label>
            <input
              type="number"
              value={smtpValues.smtp_port ?? ""}
              onChange={(e) => updateSmtp("smtp_port", e.target.value)}
              placeholder="465"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">发件邮箱</label>
            <input
              type="email"
              value={smtpValues.smtp_user ?? ""}
              onChange={(e) => updateSmtp("smtp_user", e.target.value)}
              placeholder="example@qq.com"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">授权码 / 密码</label>
            <input
              type="password"
              value={smtpValues.smtp_password ?? ""}
              onChange={(e) => updateSmtp("smtp_password", e.target.value)}
              placeholder="QQ 邮箱授权码"
              autoComplete="new-password"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">发件人显示名</label>
            <input
              type="text"
              value={smtpValues.smtp_from_name ?? ""}
              onChange={(e) => updateSmtp("smtp_from_name", e.target.value)}
              placeholder="协和医疗"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">使用 SSL</label>
            <select
              value={(smtpValues.smtp_use_ssl ?? "true").toLowerCase() === "true" ? "true" : "false"}
              onChange={(e) => updateSmtp("smtp_use_ssl", e.target.value)}
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0] bg-white cursor-pointer"
            >
              <option value="true">是（465 端口推荐）</option>
              <option value="false">否（25/587 + STARTTLS）</option>
            </select>
          </div>
        </div>
      </div>

      {/* 网络代理（订阅抓取国外站点） */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#1A2B3C]">网络代理（订阅抓取）</h3>
            <p className="text-xs text-[#718096] mt-1">
              订阅任务抓取 FDA / EMA / WHO 等海外站点时经此 HTTP 代理出网；保存后实时生效，无需重启服务。
            </p>
          </div>
          <button
            onClick={saveProxy}
            disabled={!proxyDirty || proxySaving}
            className={`flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg whitespace-nowrap ${
              !proxyDirty || proxySaving
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-[#0EA5A0] text-white hover:bg-[#0D9590] cursor-pointer"
            }`}
          >
            {proxySaving ? <RIcon name="loader-4-line" className="animate-spin" /> : <RIcon name="save-line" />}
            保存
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">启用代理</label>
            <select
              value={(proxyValues.proxy_enabled ?? "true").toLowerCase() === "false" ? "false" : "true"}
              onChange={(e) => updateProxy("proxy_enabled", e.target.value)}
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0] bg-white cursor-pointer"
            >
              <option value="true">是（命中外网域名走代理）</option>
              <option value="false">否（一律直连，仅用于本地调试）</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">代理地址（HTTP_PROXY）</label>
            <input
              type="text"
              value={proxyValues.http_proxy_url ?? ""}
              onChange={(e) => updateProxy("http_proxy_url", e.target.value)}
              placeholder="http://192.168.3.82:7890"
              className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:border-[#0EA5A0]"
            />
            <p className="text-xs text-[#A0AEC0] mt-1">支持 http:// 或 socks5:// 前缀，端口由 VPN/代理服务决定。</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#4A5568] mb-1.5">绕过代理域名（NO_PROXY）</label>
            <textarea
              value={proxyValues.proxy_no_proxy ?? ""}
              onChange={(e) => updateProxy("proxy_no_proxy", e.target.value)}
              placeholder="localhost,127.0.0.1,.cluster.local,.svc,.cn"
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0EA5A0] font-mono"
            />
            <p className="text-xs text-[#A0AEC0] mt-1">
              逗号分隔；以 <code>.</code> 开头表示后缀匹配（如 <code>.cn</code> 匹配所有 cn 域名）。
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
